import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { ncService } from '../services/api';
import API_URL from '../config';
import ModoToggle from './ModoToggle';
import NCImporter from './NCImporter';
import NCImportReview from './NCImportReview';
import PageHeader from './ui/PageHeader';
import Button from './ui/Button';
import { recordFormDraft, clearFormDraft, peekPersistedFormDraft } from '../utils/ncDraftStore'; // #215
import './NCForm.css';

const SESSION_KEY = 'nc_import_draft';

const SERVICE_SECTOR_VALUES = new Set([
  'contabil', 'saude', 'logistica', 'facilities', 'laboratorios', 'ti',
  'engenharia_projetos', 'servicos',
  'contábil / fiscal', 'saúde / clínicas', 'logística / armazenagem (3pl)',
  'facilities / terceirizados', 'laboratórios (calibração/ensaios)', 'ti / software',
  'engenharia / projetos', 'serviços',
]);
const isServiceSector = (industria) =>
  industria != null && industria !== '' && SERVICE_SECTOR_VALUES.has(String(industria).toLowerCase().trim());

// #47-coach Parte A — copy do CHECKLIST de completude FACTUAL (substitui a muleta
// 6M-causal). Régua v3 + red-team (3 correções):
//  • SÓ fatos observáveis — ZERO categoria causal/6M (sem "máquina/material/método/
//    medição/causa/o que mudou"); as MESMAS perguntas servem serviço e indústria.
//  • QUANTO/ONDE são SEMPRE-OPCIONAIS ("se aplicável") — nunca cobram vazio, nunca
//    chaveiam por setor (industria é nula com frequência → setor-gate falharia no serviço).
//  • "Onde achar" nomeia o registro do PRÓPRIO usuário (sistema/planilha/agenda), NUNCA
//    o artefato de chão de fábrica (OS/apontamento/posto/linha/roteiro).
//  • SAIU o "já pensou em outra possibilidade?" (induzia inventar 2ª hipótese; redundante
//    com o campo de suspeita e com o motor, que já aprofunda).
// Exportados p/ o teste de glossário-proibido (anti-regressão).
export const COACH_CHECKLIST = [
  'O QUÊ apareceu de errado — o desvio concreto que você observou',
  'QUANDO aconteceu ou foi percebido',
  'QUANTO (se aplicável): a extensão — quantos itens, casos, dias, pessoas ou ocorrências',
  'ONDE (se aplicável): em que ponto do seu trabalho apareceu — a etapa, a área ou o momento',
  'IMPACTO: o efeito — para o cliente, para o trabalho, em tempo ou em custo',
];
export const COACH_ONDE_ACHAR =
  'Não tem tudo à mão? Esses dados costumam estar onde você já registra o que foi feito — o sistema, a planilha ou a agenda do dia.';
// O campo de suspeita É legitimamente sobre causa (mas default-vazio, zero opção) — fica
// FORA do glossário-proibido (que governa só o checklist factual acima).
export const COACH_SUSPEITA_LABEL = 'Já sabe o que causou? (quase sempre não — e tudo bem)';
export const COACH_SUSPEITA_MICRO =
  'Deixar em branco é o esperado — descobrir a causa é o trabalho da ferramenta. Se tiver um palpite, escreva em poucas palavras; a IA testa, não toma como verdade.';

function NCForm({ onSuccess, clienteId, user }) {
  // v4.3.0 Feature 1 — estados para fluxo de importação
  const [showImporter, setShowImporter] = useState(false);
  const [importedData, setImportedData] = useState(null);
  const [importedFile, setImportedFile] = useState(null);

  // Recuperar draft de sessionStorage ao montar (refresh acidental dentro da TTL)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (raw) {
        const draft = JSON.parse(raw);
        if (draft && draft.expires && draft.expires > Date.now()) {
          setImportedData(draft.data);
          // arquivo original não cabe em sessionStorage; usuário precisa re-anexar manualmente
        } else {
          sessionStorage.removeItem(SESSION_KEY);
        }
      }
    } catch (_) {
      // ignore
    }
  }, []);

  const handleExtracted = (result, file) => {
    setImportedData(result);
    setImportedFile(file);
    setShowImporter(false);
  };

  const handleCancelImport = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setImportedData(null);
    setImportedFile(null);
  };

  const [formData, setFormData] = useState({
    cliente_id: clienteId || 1,
    nome_cliente: '',
    numero_nc: '',
    produto: '',
    lote: '',
    descricao_nc: '',
    contexto_processo: '',
    // #47-coach Parte A — suspeita de causa do registrante (input qualificado LIMPO,
    // default-vazio). Só aparece/é enviado com a flag COACH_INPUT_HABILITADO ON.
    suspeita_causa_registrante: '',
    industria: '',
    contexto_setorial_custom: '',
    data_ocorrencia: '',
    origem: '',
    quantidade_afetada: '',
    status_lote: '',
    // Sprint 5A A9 (B36+B37+B42) — campos de origem_tipo e NC externa.
    // origem_tipo é "quem emitiu" (proprio/cliente/fornecedor); NÃO confundir
    // com `origem` acima (Inspeção/Auditoria/...) que é "como foi detectada".
    origem_tipo: 'proprio',
    cliente_emissor: '',
    numero_nc_cliente: '',
    prazo_resposta_cliente: '',
    contato_cliente_email: '',
    // #64 outbound — nome do fornecedor envolvido (quando origem=fornecedor).
    // Backend resolve/cria entidade Fornecedor + seta nc.fornecedor_id.
    fornecedor_nome: '',
  });

  const [modoAnalise, setModoAnalise] = useState('registro');
  const [files, setFiles] = useState([]);

  // #161/#226 — gating de tier (flag-aware, vindo do /auth/me via App).
  // Defaults conservadores (libera) garantem comportamento atual quando o
  // sinal não está presente (flag OFF no backend ⇒ liberada=true/degust=false).
  const investigacaoLiberada = user?.investigacao_liberada !== false;
  const degustacaoDisponivel = !!user?.degustacao_disponivel;
  const bloqueioMotivo = user?.investigacao_bloqueio_motivo || null;

  // #47-coach Parte A — flag global (vinda do /auth/me). OFF = muleta 6M-causal de hoje
  // (idêntico). ON = checklist de completude FACTUAL + campo de suspeita limpo.
  const coachInput = user?.coach_input_habilitado === true;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [industrias, setIndustrias] = useState([]);

  // Felipe F1.1 + F1.2 (25/05/26): opção "Outro" com texto livre nos
  // dropdowns de "Como foi detectada" e "Indústria/Setor". Sentinel
  // value '__outro__' aparece como opção no select; quando selecionado,
  // o input texto livre fica visível e seu valor sobrescreve o do select
  // no submit (backend já aceita string livre em ambos os campos).
  const [origemCustom, setOrigemCustom] = useState('');
  const [industriaCustom, setIndustriaCustom] = useState('');
  const OUTRO_SENTINEL = '__outro__';
  // "setor sempre da conta" (jun/2026) — o campo "Indústria/Setor" por-NC foi
  // APOSENTADO: o setor que ancora a análise é o da CONTA (perfil da empresa),
  // não um override por-NC (que confundia e podia capturar o setor do cliente).
  // Mantemos o bloco no código (gated false) p/ reversão trivial; a NC nasce com
  // industria='' e o backend resolve o setor pela conta (industria_efetiva).
  const SHOW_INDUSTRIA_FIELD = false;

  // #215 — preservação contra queda de sessão (espelha o #145 do NCEditor):
  // o estado do form fica registrado em memória; se os interceptors de auth
  // forçarem logout (refresh token vencido), o flush persiste e, ao voltar,
  // oferecemos restaurar. Arquivos não sobrevivem (File não serializa).
  const [formDraftDisponivel, setFormDraftDisponivel] = useState(() => peekPersistedFormDraft());

  useEffect(() => {
    const digitouAlgo = ['descricao_nc', 'produto', 'lote', 'contexto_processo',
      'contexto_setorial_custom', 'nome_cliente', 'cliente_emissor', 'fornecedor_nome']
      .some((k) => (formData[k] || '').trim());
    if (digitouAlgo) {
      recordFormDraft({ formData, modoAnalise, origemCustom, industriaCustom });
    }
  }, [formData, modoAnalise, origemCustom, industriaCustom]);

  // #161/#226 — se a Investigação está travada pro plano (ou a degustação
  // esgotou) e o modo selecionado ficou em 'investigacao' (ex.: restaurado de
  // rascunho), volta pra 'registro'. Backend também gateia (403) — defesa em
  // profundidade. Com a flag OFF, investigacaoLiberada=true ⇒ no-op.
  useEffect(() => {
    if (!investigacaoLiberada && modoAnalise === 'investigacao') {
      setModoAnalise('registro');
    }
  }, [investigacaoLiberada, modoAnalise]);

  const handleRestaurarFormDraft = () => {
    const d = formDraftDisponivel;
    if (d?.payload) {
      setFormData((prev) => ({ ...prev, ...d.payload.formData, cliente_id: clienteId || d.payload.formData.cliente_id }));
      if (d.payload.modoAnalise) setModoAnalise(d.payload.modoAnalise);
      setOrigemCustom(d.payload.origemCustom || '');
      setIndustriaCustom(d.payload.industriaCustom || '');
    }
    clearFormDraft();
    setFormDraftDisponivel(null);
  };

  const handleDescartarFormDraft = () => {
    clearFormDraft();
    setFormDraftDisponivel(null);
  };

  // Atualizar cliente_id quando mudar
  useEffect(() => {
    if (clienteId) {
      setFormData(prev => ({ ...prev, cliente_id: clienteId }));
    }
  }, [clienteId]);

  // #100: fornecedores já cadastrados do tenant → autocomplete no campo
  // "Fornecedor envolvido". Ajuda o usuário a REUSAR o existente em vez de
  // digitar uma variação ("Basile" vs "BASILE LTDA") e criar quase-duplicata.
  const [fornecedoresSugestoes, setFornecedoresSugestoes] = useState([]);
  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const data = await ncService.listFornecedores(clienteId);
        if (!cancelado) setFornecedoresSugestoes(data.fornecedores || []);
      } catch (e) { /* sem sugestões — input segue livre */ }
    })();
    return () => { cancelado = true; };
  }, [clienteId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      setFiles((prev) => [...prev, ...acceptedFiles]);
    },
  });

  // Buscar indústrias disponíveis
  useEffect(() => {
    const fetchIndustrias = async () => {
      try {
        const response = await fetch(`${API_URL}/api/v2/nc/industrias`);
        const data = await response.json();
        setIndustrias(data.industrias || []);
      } catch (err) {
        console.error('Erro ao buscar indústrias:', err);
      }
    };
    fetchIndustrias();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const submitData = new FormData();

      // Sprint 5A A9: campos exclusivos de NC externa só vão ao backend
      // quando origem_tipo === 'cliente'. Se usuário preencher e depois
      // mudar pra 'proprio', mantemos no state mas filtramos no submit
      // (não perde input do usuário caso ele volte a marcar 'cliente').
      const CAMPOS_CLIENTE = [
        'cliente_emissor',
        'numero_nc_cliente',
        'prazo_resposta_cliente',
        'contato_cliente_email',
      ];

      // Felipe F1.1 + F1.2: se origem/industria for sentinel "Outro",
      // sobrescreve com o texto livre. Validação: precisa ter texto não-vazio
      // se sentinel selecionado (senão envia string vazia = como se nada
      // fosse selecionado, comportamento aceitável).
      const origemFinal = formData.origem === OUTRO_SENTINEL
        ? origemCustom.trim()
        : formData.origem;
      const industriaFinal = formData.industria === OUTRO_SENTINEL
        ? industriaCustom.trim()
        : formData.industria;

      Object.keys(formData).forEach((key) => {
        if (CAMPOS_CLIENTE.includes(key) && formData.origem_tipo !== 'cliente') return;
        // #64 outbound — fornecedor_nome só vai quando origem=fornecedor
        // (preserva input se user alternar radio, sem vazar pro backend).
        if (key === 'fornecedor_nome' && formData.origem_tipo !== 'fornecedor') return;
        // origem_tipo SEMPRE vai (mesmo 'proprio') pra o backend saber
        // explicitamente que NC é interna. Demais campos: filtro de truthy.
        if (key === 'origem_tipo') {
          submitData.append(key, formData[key] || 'proprio');
          return;
        }
        // Skip — origem e industria são tratados separadamente abaixo
        // pra suportar a opção "Outro" com texto livre.
        if (key === 'origem' || key === 'industria') return;
        if (formData[key]) submitData.append(key, formData[key]);
      });
      if (origemFinal) submitData.append('origem', origemFinal);
      if (industriaFinal) submitData.append('industria', industriaFinal);
      submitData.append('modo_analise', modoAnalise);

      // Debug: Log dos campos sendo enviados
      console.log('=== ENVIANDO NC ===');
      console.log('FormData keys:', Array.from(submitData.keys()));
      console.log('Indústria:', formData.industria);
      console.log('Contexto Setorial:', formData.contexto_setorial_custom);

      if (files.length > 0) {
        files.forEach((file) => submitData.append('evidencias', file));
      }

      const result = await ncService.processarNC(submitData);
      clearFormDraft(); // #215 — enviado com sucesso, rascunho obsoleto
      if (onSuccess) onSuccess(result);
    } catch (err) {
      console.error('Erro completo:', err);

      // #215 — sessão expirada: mensagem humana em vez do jargão técnico.
      // O rascunho já está registrado em memória; se o interceptor forçar
      // logout, ele é persistido e oferecido de volta após o re-login.
      if (err.response?.status === 401) {
        setError('Sua sessão expirou. Entre novamente — o que você preencheu aqui fica guardado e será oferecido de volta (anexos precisam ser re-anexados).');
        setLoading(false);
        return;
      }

      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          const msgs = detail.map((e) => {
            const field = e.loc ? e.loc.join('.') : 'campo';
            return `${field}: ${e.msg}`;
          }).join('; ');
          setError(`Erro de validação: ${msgs}`);
        } else if (typeof detail === 'string') {
          setError(detail);
        } else {
          setError(JSON.stringify(detail));
        }
      } else if (err.message) {
        setError(`Erro: ${err.message}`);
      } else {
        setError('Erro ao processar NC. Verifique os dados e tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  // v4.3.0 Feature 1 — se há dados importados, mostra revisão em vez do form normal.
  // Este early return precisa ficar DEPOIS de todos os hooks (rules-of-hooks).
  if (importedData) {
    return (
      <NCImportReview
        extractedData={importedData}
        originalFile={importedFile}
        clienteId={clienteId || 1}
        onCancel={handleCancelImport}
        onCreated={(result) => {
          sessionStorage.removeItem(SESSION_KEY);
          setImportedData(null);
          setImportedFile(null);
          if (onSuccess) onSuccess(result);
        }}
      />
    );
  }

  return (
    <div className="nc-form-container">
      {/* Header */}
      <PageHeader
        title="Registro de Não-Conformidade"
        subtitle="Documente desvios de qualidade e processos para análise e ações corretivas imediatas. O sistema irá gerar um ID único após o envio."
        actions={
          <Button
            variant="secondary"
            size="sm"
            icon="upload_file"
            onClick={() => setShowImporter(true)}
            title="Importar formulário de NC recebido de cliente"
          >
            Importar formulário
          </Button>
        }
      />

      {showImporter && (
        <NCImporter
          onClose={() => setShowImporter(false)}
          onExtracted={handleExtracted}
        />
      )}

      {/* Form card */}
      <div className="nc-form-card">
        <span className="nc-form-badge">Formulário de Entrada de Dados</span>

        {error && <div className="nc-form-error">{error}</div>}

        {/* #215 — formulário preservado de uma queda de sessão (espelha o
            "restaurar rascunho?" do NCEditor/#145). Só aparece quando o
            logout foi forçado COM texto digitado — zero falso positivo. */}
        {formDraftDisponivel && (
          <div style={{ marginBottom: '16px', padding: '12px 16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span className="material-symbols-outlined" style={{ color: '#92400e' }}>history</span>
            <span style={{ flex: 1, minWidth: '200px', fontSize: '13px', color: '#92400e' }}>
              Sua sessão expirou enquanto você preenchia este formulário — recuperamos o que foi digitado. Anexos precisam ser re-anexados.
            </span>
            <Button size="sm" onClick={handleRestaurarFormDraft}>Restaurar</Button>
            <Button size="sm" variant="secondary" onClick={handleDescartarFormDraft}>Descartar</Button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Sprint 5A A9 (B36+B37+B42) — radio origem_tipo no topo do form.
              Difere do select `origem` (renomeado abaixo pra "Como foi detectada"):
              aqui é "quem emitiu" a NC; lá é "como foi detectada". */}
          <div className="nc-form-group" style={{ marginBottom: '16px', padding: '12px 16px', background: '#f8fafc', borderLeft: '3px solid #006948', borderRadius: '6px' }}>
            <label className="nc-form-label" style={{ marginBottom: '8px' }}>Origem da NC *</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="origem_tipo"
                  value="proprio"
                  checked={formData.origem_tipo === 'proprio'}
                  onChange={handleChange}
                />
                <span><strong>Interna (própria)</strong> — detectada pela equipe</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="origem_tipo"
                  value="cliente"
                  checked={formData.origem_tipo === 'cliente'}
                  onChange={handleChange}
                />
                <span><strong>Cliente externo</strong> — um cliente reclamou de você</span>
              </label>
              {/* #64 Feature 3 — habilitado. origem_tipo='fornecedor' é só
                  etiqueta (quem causou). O convite ao fornecedor é ação
                  pós-análise na seção "Resposta de fornecedor" do resultado. */}
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="origem_tipo"
                  value="fornecedor"
                  checked={formData.origem_tipo === 'fornecedor'}
                  onChange={handleChange}
                />
                <span><strong>Fornecedor</strong> — veio de um material/serviço que você comprou</span>
              </label>
            </div>
            <small style={{ fontSize: '11px', color: '#57657a', marginTop: '6px', display: 'block' }}>
              Quem está apontando o problema? Interna = sua equipe achou; Cliente externo = um cliente reclamou; Fornecedor = veio de um material/serviço que você comprou. (Diferente de <em>como</em> foi detectada, mais abaixo.)
            </small>
          </div>

          {/* Sprint 5A A9: campos exclusivos quando origem_tipo === 'cliente' */}
          {formData.origem_tipo === 'cliente' && (
            <div className="nc-form-grid" style={{ marginBottom: '16px', padding: '12px 16px', background: '#fef3c7', borderLeft: '3px solid #f59e0b', borderRadius: '6px' }}>
              <div className="nc-form-group">
                <label className="nc-form-label">Cliente que reclamou *</label>
                <input
                  className="nc-form-input"
                  type="text"
                  name="cliente_emissor"
                  value={formData.cliente_emissor}
                  onChange={handleChange}
                  placeholder="Empresa que reclamou. Ex: COMERCIAL NORTE SUL LTDA"
                  required
                />
                <small style={{ fontSize: '11px', color: '#57657a', marginTop: '4px', display: 'block' }}>
                  Nome da empresa que abriu a reclamação contra você.
                </small>
              </div>
              <div className="nc-form-group">
                <label className="nc-form-label">Nº da NC no cliente</label>
                <input
                  className="nc-form-input"
                  type="text"
                  name="numero_nc_cliente"
                  value={formData.numero_nc_cliente}
                  onChange={handleChange}
                  placeholder="Ex: 015/2026"
                />
              </div>
              <div className="nc-form-group">
                <label className="nc-form-label">Prazo de resposta</label>
                <input
                  className="nc-form-input"
                  type="date"
                  name="prazo_resposta_cliente"
                  value={formData.prazo_resposta_cliente}
                  onChange={handleChange}
                />
              </div>
              <div className="nc-form-group">
                <label className="nc-form-label">Email de contato</label>
                <input
                  className="nc-form-input"
                  type="email"
                  name="contato_cliente_email"
                  value={formData.contato_cliente_email}
                  onChange={handleChange}
                  placeholder="qualidade@cliente.com.br"
                />
              </div>
            </div>
          )}

          {/* Grid 2 colunas */}
          <div className="nc-form-grid">
            {/* Cliente ID é injetado automaticamente pelo App via prop clienteId — não exibir input */}
            <input type="hidden" name="cliente_id" value={formData.cliente_id} />

            <div className="nc-form-group">
              <label className="nc-form-label">Número da NC</label>
              <input
                className="nc-form-input"
                type="text"
                name="numero_nc"
                value={formData.numero_nc}
                onChange={handleChange}
                placeholder="Ex: RNC-006/2026 (deixe em branco para gerar automaticamente)"
              />
              <small style={{ fontSize: '11px', color: '#57657a', marginTop: '4px', display: 'block' }}>
                Opcional — se não preenchido, o sistema gera automaticamente (RNC-{new Date().getFullYear()}-0001)
              </small>
            </div>

            {/* Sprint 5A A9: nome_cliente só aparece quando NC é interna.
                Pra NC externa (origem_tipo='cliente') o campo correto é
                cliente_emissor (no bloco condicional acima). State NÃO é
                removido — só rendering condicional — pra preservar fluxos
                legados que possam depender dele. */}
            {/* #64 outbound: "Cliente que detectou" só faz sentido em NC
                interna (proprio). No outbound (fornecedor) usamos o campo
                "Fornecedor envolvido" abaixo. */}
            {formData.origem_tipo === 'proprio' && (
              <div className="nc-form-group">
                <label className="nc-form-label">Cliente que detectou (opcional)</label>
                <input
                  className="nc-form-input"
                  type="text"
                  name="nome_cliente"
                  value={formData.nome_cliente}
                  onChange={handleChange}
                  placeholder="Ex: ABC Refrigeração Ltda — quem reportou. Para NC externa, use o radio acima."
                />
              </div>
            )}

            {/* #64 outbound — campo do fornecedor envolvido. Backend resolve/
                cria a entidade Fornecedor e alimenta o tom da análise IA
                (que trata a causa raiz como hipótese a confirmar). */}
            {formData.origem_tipo === 'fornecedor' && (
              <div className="nc-form-group" style={{ padding: '12px 16px', background: '#fef3c7', borderLeft: '3px solid #f59e0b', borderRadius: '6px' }}>
                <label className="nc-form-label">Fornecedor envolvido *</label>
                <input
                  className="nc-form-input"
                  type="text"
                  name="fornecedor_nome"
                  value={formData.fornecedor_nome}
                  onChange={handleChange}
                  placeholder="Ex: Aciaria Vale Verde Ltda — o fornecedor que entregou o material não conforme"
                  list="nc-fornecedores-sugestoes"
                  autoComplete="off"
                />
                {/* #100: autocomplete dos fornecedores já cadastrados — reusar
                    o existente evita criar quase-duplicata. */}
                <datalist id="nc-fornecedores-sugestoes">
                  {fornecedoresSugestoes.map((f) => (
                    <option key={f.id} value={f.nome} />
                  ))}
                </datalist>
                <p style={{ fontSize: '12px', color: '#92400e', margin: '6px 0 0' }}>
                  A análise da IA tratará a causa raiz como <strong>hipótese a confirmar com o fornecedor</strong> —
                  você não tem acesso ao processo interno dele. Depois da análise, use "Convidar fornecedor"
                  no resultado pra pedir a resposta dele.
                </p>
              </div>
            )}

            <div className="nc-form-group">
              <label className="nc-form-label">Produto / Serviço</label>
              <input
                className="nc-form-input"
                type="text"
                name="produto"
                value={formData.produto}
                onChange={handleChange}
                placeholder="Nome do Produto ou Serviço"
              />
            </div>

            {!isServiceSector(formData.industria) && (
              <div className="nc-form-group">
                <label className="nc-form-label">Lote (Batch)</label>
                <input
                  className="nc-form-input"
                  type="text"
                  name="lote"
                  value={formData.lote}
                  onChange={handleChange}
                  placeholder="Identificação do Lote"
                />
              </div>
            )}

            <div className="nc-form-group">
              <label className="nc-form-label">Data da Ocorrência</label>
              <input
                className="nc-form-input"
                type="date"
                name="data_ocorrencia"
                value={formData.data_ocorrencia}
                onChange={handleChange}
              />
            </div>

            {/* Sprint 5A A9: renomeado de "Origem da NC" pra "Como foi detectada"
                pra desambiguar do radio origem_tipo no topo do form. Chave do
                state e do payload continuam `origem` (compat com backend). */}
            <div className="nc-form-group">
              <label className="nc-form-label">Como foi detectada</label>
              <select
                className="nc-form-input"
                name="origem"
                value={formData.origem}
                onChange={handleChange}
              >
                <option value="">Selecione...</option>
                <option value="Inspeção">Inspeção</option>
                <option value="Cliente">Cliente</option>
                <option value="Auditoria">Auditoria</option>
                <option value="Produção">Produção</option>
                <option value="Recebimento">Recebimento</option>
                <option value="Expedição">Expedição</option>
                <option value="Laboratório">Laboratório</option>
                <option value={OUTRO_SENTINEL}>Outro...</option>
              </select>
              {formData.origem === OUTRO_SENTINEL && (
                <input
                  className="nc-form-input"
                  type="text"
                  name="origem_custom"
                  value={origemCustom}
                  onChange={(e) => setOrigemCustom(e.target.value)}
                  placeholder="Descreva como foi detectada"
                  style={{ marginTop: '8px' }}
                  maxLength={100}
                />
              )}
              <small style={{ fontSize: '11px', color: '#57657a', marginTop: '4px', display: 'block' }}>
                Em que momento o problema apareceu? Ex: inspeção, recebimento, produção, auditoria. (Onde/quando foi pego — não é quem reclama.)
              </small>
            </div>

            <div className="nc-form-group">
              <label className="nc-form-label">Quantidade Afetada</label>
              <input
                className="nc-form-input"
                type="text"
                name="quantidade_afetada"
                value={formData.quantidade_afetada}
                onChange={handleChange}
                placeholder="Ex: 50 peças, 2 lotes, 100kg"
              />
            </div>

            {!isServiceSector(formData.industria) && (
              <div className="nc-form-group">
                <label className="nc-form-label" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  Status do Lote
                  <span
                    className="material-symbols-outlined"
                    title={'O que está acontecendo com o material agora:\n• Bloqueado = parado, não pode usar\n• Liberado = liberado para uso\n• Em análise = ainda sendo avaliado\n• Segregado = separado do material bom\n• Retrabalhado = sendo consertado\n• Sucateado = descartado'}
                    role="img"
                    aria-label={'O que está acontecendo com o material agora: Bloqueado = parado; Liberado = liberado para uso; Em análise = ainda sendo avaliado; Segregado = separado do material bom; Retrabalhado = sendo consertado; Sucateado = descartado'}
                    tabIndex={0}
                    style={{ fontSize: '16px', color: '#6a737d', cursor: 'help' }}
                  >
                    help
                  </span>
                </label>
                <select
                  className="nc-form-input"
                  name="status_lote"
                  value={formData.status_lote}
                  onChange={handleChange}
                >
                  <option value="">Selecione...</option>
                  <option value="Bloqueado">Bloqueado</option>
                  <option value="Liberado">Liberado</option>
                  <option value="Em análise">Em análise</option>
                  <option value="Segregado">Segregado</option>
                  <option value="Retrabalhado">Retrabalhado</option>
                  <option value="Sucateado">Sucateado</option>
                </select>
              </div>
            )}

            {/* "setor sempre da conta" (jun/2026): campo "Indústria/Setor" por-NC
                APOSENTADO (SHOW_INDUSTRIA_FIELD=false). Era redundante (o setor do
                tenant já está no perfil da conta) e confuso. O bloco fica gated p/
                reversão trivial. (#64 outbound também já o escondia.) */}
            {SHOW_INDUSTRIA_FIELD && formData.origem_tipo !== 'fornecedor' && (
            <div className="nc-form-group">
              <label className="nc-form-label">Indústria / Setor</label>
              <select
                className="nc-form-input"
                name="industria"
                value={formData.industria}
                onChange={handleChange}
              >
                <option value="">Selecione a Indústria...</option>
                {industrias.map((ind) => (
                  <option key={ind.codigo} value={ind.codigo}>
                    {ind.nome}
                  </option>
                ))}
                <option value={OUTRO_SENTINEL}>Outro...</option>
              </select>
              {formData.industria === OUTRO_SENTINEL && (
                <input
                  className="nc-form-input"
                  type="text"
                  name="industria_custom"
                  value={industriaCustom}
                  onChange={(e) => setIndustriaCustom(e.target.value)}
                  placeholder="Descreva o setor da sua empresa"
                  style={{ marginTop: '8px' }}
                  maxLength={100}
                />
              )}
              <small style={{ fontSize: '11px', color: '#57657a', marginTop: '4px', display: 'block' }}>
                Selecione o setor da <strong>sua empresa</strong> — a IA buscará a causa raiz no seu processo produtivo
              </small>
            </div>
            )}
          </div>

          {/* Textareas */}
          <div className="nc-form-textareas">
            <div className="nc-form-group">
              <label className="nc-form-label">Descrição da NC *</label>
              <textarea
                className="nc-form-input"
                name="descricao_nc"
                value={formData.descricao_nc}
                onChange={handleChange}
                rows="4"
                required
                placeholder="Ex: Lote de 200 vedações com bolhas, detectadas na inspeção final…"
              />
              <small style={{ fontSize: '11px', color: '#57657a', marginTop: '4px', display: 'block' }}>
                Conte o que deu errado como pra um colega: <strong>o quê, onde, quando e por que é problema</strong>. Quanto mais detalhes, melhor a análise.
              </small>
            </div>

            {/* #64 outbound: "Situação atual do processo" ESCONDIDO no outbound.
                Decisão Francis — pede info do processo produtivo onde a falha
                ocorreu, mas no outbound esse processo é do FORNECEDOR (terceiro),
                não do tenant. Pedir ao tenant é incoerente. Modos proprio/cliente
                mantêm o campo + helper 6M. */}
            {formData.origem_tipo !== 'fornecedor' && (
            <div className="nc-form-group">
              <label className="nc-form-label">Situação atual do processo</label>
              <textarea
                className="nc-form-input"
                name="contexto_processo"
                value={formData.contexto_processo}
                onChange={handleChange}
                rows="4"
                placeholder="Ex: a peça é cortada numa máquina semiautomática; existe instrução de trabalho impressa no posto, mas nem sempre é seguida na correria; o operador entrou há 1 mês. Conte o seu caso assim — com o máximo de detalhe que puder."
              />
              <small style={{ fontSize: '11px', color: '#57657a', marginTop: '4px', display: 'block' }}>
                Conte como o seu processo funciona e o que pode ter influenciado o problema. É o campo que mais melhora a análise — você revisa o resultado depois.
              </small>
              {coachInput ? (
                <>
                  {/* #47-coach Parte A (flag ON) — CHECKLIST de completude FACTUAL: só
                      fatos observáveis, ZERO categoria causal/6M. Substitui a muleta que
                      plantava causa. Copy nas const COACH_* (testadas no glossário). */}
                  <div style={{
                    marginTop: '8px', padding: '10px 12px', background: '#f0f7ff',
                    borderLeft: '3px solid #4a90e2', borderRadius: '3px', fontSize: '12px',
                    color: '#2c5282', lineHeight: '1.6',
                  }}>
                    <strong>O que você OBSERVOU — quanto mais fato, melhor a análise:</strong>
                    <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>
                      {COACH_CHECKLIST.map((item, i) => (<li key={i}>{item}</li>))}
                    </ul>
                    <div style={{ marginTop: '8px', fontStyle: 'italic', color: '#475569' }}>
                      {COACH_ONDE_ACHAR}
                    </div>
                  </div>
                  {/* Campo de SUSPEITA limpo — default-vazio, zero opção de causa. A IA
                      testa o palpite (não toma como verdade); o motor já aprofunda além. */}
                  <div style={{ marginTop: '10px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                      {COACH_SUSPEITA_LABEL}
                    </label>
                    <textarea
                      name="suspeita_causa_registrante"
                      value={formData.suspeita_causa_registrante}
                      onChange={handleChange}
                      rows={2}
                      placeholder="(opcional — pode deixar em branco)"
                      style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '4px', resize: 'vertical', boxSizing: 'border-box' }}
                    />
                    <small style={{ fontSize: '11px', color: '#64748b', display: 'block', marginTop: '4px' }}>
                      {COACH_SUSPEITA_MICRO}
                    </small>
                  </div>
                </>
              ) : (
                <>
                  {/*
                    [muleta legada — flag COACH_INPUT_HABILITADO OFF, idêntico ao de hoje]
                    Coaching de contexto REFORÇADO (Francis 15/06): concreto pra leigo
                    + a IA analisa SÓ com o que for informado. Substitui o antigo helper 6M.
                  */}
                  <div style={{
                    marginTop: '8px',
                    padding: '10px 12px',
                    background: '#f0f7ff',
                    borderLeft: '3px solid #4a90e2',
                    borderRadius: '3px',
                    fontSize: '12px',
                    color: '#2c5282',
                    lineHeight: '1.6',
                  }}>
                    <strong>O que ajuda a IA a achar a causa — descreva o que souber:</strong>
                    <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>
                      <li>Como o processo funciona, na prática (do começo ao fim do que deu errado)</li>
                      <li>Se a etapa é feita por máquinas/equipamentos ou à mão, por uma pessoa (ou os dois)</li>
                      <li>Se existe uma instrução de trabalho / procedimento escrito para essa etapa — ou se ela é feita "de cabeça"</li>
                      <li>Se essa instrução é seguida no dia a dia — ou se costuma ter atalho/improviso</li>
                      <li>Se quem faz é treinado e/ou experiente (ex: há quanto tempo na função, recebeu treinamento)</li>
                      <li>Tudo o que pode ter influenciado: troca de turno, pressa, equipamento parado, material diferente, mudança recente no processo</li>
                    </ul>
                  </div>
                  <div style={{
                    marginTop: '8px',
                    padding: '10px 12px',
                    background: '#fffbeb',
                    borderLeft: '3px solid #f59e0b',
                    borderRadius: '3px',
                    fontSize: '12px',
                    color: '#92400e',
                    lineHeight: '1.6',
                  }}>
                    A IA analisa <strong>apenas com as informações que você escrever aqui</strong> — ela não conhece a sua
                    empresa nem visita o seu processo. Por isso, quanto mais detalhe você der, melhor e mais certeira fica a
                    análise. E se você já desconfia da causa, diga aqui — a IA usa essa pista, sem ficar presa a ela.
                  </div>
                </>
              )}
            </div>
            )}

            {/* #64 outbound: contexto_setorial_custom MANTIDO e RENOMEADO no
                outbound. Decisão Francis — é o campo onde o tenant tem info real
                (uso interno do material), preserva sinal observável + o +4 de
                score via fallback. Nota técnica: candidato a alimentar
                severidade ponderada / custo estimado (#51/#52). */}
            <div className="nc-form-group">
              <label className="nc-form-label">
                {formData.origem_tipo === 'fornecedor'
                  ? 'Aplicação do material no seu processo'
                  : 'Contexto da Aplicação no Cliente (Opcional)'}
              </label>
              <textarea
                className="nc-form-input"
                name="contexto_setorial_custom"
                value={formData.contexto_setorial_custom}
                onChange={handleChange}
                rows="2"
                placeholder={formData.origem_tipo === 'fornecedor'
                  ? 'Como o material recebido é usado internamente e o risco em caso de falha. Ex: vigotas estruturais de laje em obra residencial, exigência NBR 6118, fissuras comprometem capacidade de carga.'
                  : 'Como o seu produto é usado pelo cliente e qual o risco da NC para ele. Ex: vedação de compressor de refrigerador doméstico, exige resistência a -20°C, norma ASTM D2000, cliente com certificação IATF.'}
              />
              <small style={{ fontSize: '11px', color: '#57657a', marginTop: '4px', display: 'block' }}>
                {formData.origem_tipo === 'fornecedor'
                  ? <>Como o material recebido é utilizado internamente — <strong>criticidade, normas exigidas, risco em caso de falha</strong>. Quanto mais detalhe, melhor a IA dimensiona impacto e severidade.</>
                  : <>Descreva a <strong>aplicação no cliente</strong> — criticidade, normas exigidas, riscos. Ajuda a IA a dimensionar o impacto e priorizar ações.</>}
              </small>
            </div>
          </div>

          {/* Dropzone */}
          <div className="nc-form-group" style={{ marginBottom: '0' }}>
            <label className="nc-form-label">Evidências (Fotos/Documentos)</label>
            <div
              {...getRootProps()}
              className={`nc-dropzone${isDragActive ? ' active' : ''}`}
            >
              <input {...getInputProps()} />
              <div className="nc-dropzone-icon material-symbols-outlined">attach_file</div>
              <p>
                {isDragActive
                  ? 'Solte os arquivos aqui...'
                  : 'Arraste arquivos aqui ou clique para selecionar'}
              </p>
              <small>PDF, PNG, JPG, DOCX, XLSX até 25MB por arquivo</small>
            </div>

            {files.length > 0 && (
              <div className="nc-files-list">
                {files.map((file, index) => (
                  <div key={index} className="nc-file-item">
                    <span>{file.name}</span>
                    <button type="button" onClick={() => removeFile(index)} aria-label="Remover arquivo">
                      <span className="material-symbols-outlined" aria-hidden="true">close</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Modo de Análise */}
          <ModoToggle
            value={modoAnalise}
            onChange={setModoAnalise}
            disabled={loading}
            investigacaoLiberada={investigacaoLiberada}
            degustacaoDisponivel={degustacaoDisponivel}
            bloqueioMotivo={bloqueioMotivo}
          />

          {/* Actions */}
          <div className="nc-form-actions">
            <Button
              variant="secondary"
              type="button"
              onClick={() => onSuccess && onSuccess(null)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
              icon={!loading && modoAnalise === 'investigacao' ? 'science' : undefined}
            >
              {loading
                ? (modoAnalise === 'investigacao' ? 'Investigando (~40s)…' : 'Processando (~15s)…')
                : (modoAnalise === 'investigacao' ? 'Iniciar Investigação Completa' : 'Registrar Não-Conformidade')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default NCForm;
